/**
 * Client-facing shapes.
 *
 * A deliberately NARROW mirror of the server contracts: the client needs what it
 * renders, not the whole ledger. Everything private — candidate rows, exact
 * source references, rejected alternatives — is absent from these types because
 * it is absent from the responses, and typing it here would invite a component
 * to reach for something the API will never send.
 */

import type {
  ACQUIRER_FIT_DIMENSION_NAMES,
  ACQUIRER_FIT_TIER_NAMES,
  COMPETITION_AXIS_NAMES,
  COMPETITOR_CLASS_NAMES,
  EXIT_PATHWAY_NAMES,
  EXIT_PATHWAY_TIER_NAMES,
  MARKET_CONCLUSION_NAMES,
  NOT_REASSESSED_REASONS,
  PESTEL_CATEGORY_NAMES,
  PESTEL_POSTURE_NAMES,
  PORTER_FORCE_NAMES,
  PORTER_RATING_NAMES,
  THESIS_COMPARISON_NAMES,
} from './report-contract';

export type {
  AcquirerCandidateV11 as AcquirerCandidate,
  CompetitionAxisV11,
  CompetitionBodyV11 as CompetitionBody,
  CompetitionEntityV11,
  ComparableTransactionV11 as ComparableTransaction,
  ExitStrategyBodyV11 as ExitStrategyBody,
  GroundedStatement,
  MarketRangeC as MarketRange,
  MarketValidationBodyV11 as MarketValidationBody,
  MaterialNumberC as MaterialNumber,
  PestelBodyV11 as PestelBody,
  PestelItemV11 as PestelItem,
  PorterBodyV11 as PorterBody,
  PorterDriverV11,
  ReportParseResult,
  StatementGround,
} from './report-contract';
export type { MarketAssumptions } from './market-assumptions';

export type AnalysisType =
  | 'competition'
  | 'porter'
  | 'pestel'
  | 'market_validation'
  | 'exit_strategy';

export const ANALYSIS_TABS: readonly AnalysisType[] = [
  'competition',
  'porter',
  'pestel',
  'market_validation',
  'exit_strategy',
];

export const ANALYSIS_LABELS: Record<AnalysisType, string> = {
  competition: 'Competition',
  porter: 'Five Forces',
  pestel: 'PESTEL',
  market_validation: 'Market Validation',
  exit_strategy: 'Exit Strategy',
};

/**
 * Tab-strip labels — short enough that all five FIT a 390pt phone.
 *
 * The approved mobile mockups use exactly these short forms. Fitting is what
 * removes the need to auto-scroll a selected tab into view, which is worth
 * more than the two extra words: the scroll had to be driven off layout
 * measurements whose ordering React Native does not guarantee, and when it
 * lost that race the strip showed Competition as though it were the selection.
 *
 * The FULL name is what every other surface uses — breadcrumb, section
 * headings, framework modal — and is what a screen reader announces, so
 * nothing is actually abbreviated away.
 */
export const ANALYSIS_TAB_LABELS: Record<AnalysisType, string> = {
  competition: 'Competition',
  porter: '5 Forces',
  pestel: 'PESTEL',
  market_validation: 'Market',
  exit_strategy: 'Exit',
};

/** Client-safe failure vocabulary. Raw provider/database/model errors never appear. */
export type SafeFailureCode =
  | 'provider_unavailable'
  | 'input_not_ready'
  | 'source_detail_unavailable'
  | 'insufficient_evidence'
  | 'invalid_model_output'
  | 'response_truncated'
  | 'claim_validation_failed'
  | 'timed_out'
  | 'consent_required'
  | 'feature_disabled'
  | 'rate_limited'
  | 'usage_limit_reached'
  /** The published body does not match the report contract. Ours, not theirs. */
  | 'report_contract_failed'
  /** A research source returned data that failed validation. Nothing published. */
  | 'invalid_provider_output'
  | 'internal_error';

/** One approved field, as an investor may see it. */
export interface ProvenancedInput {
  /**
   * ABSENT when the founder declined publication. The context row is
   * investor-readable, so a withheld value is never written to it — only the
   * marker below, which is what lets the UI say "provided, not shared" rather
   * than showing the same blank as a field nobody ever answered. See
   * supabase/functions/_shared/strategic-analysis/persisted-context.ts.
   */
  value?: unknown;
  originLabel: 'profile' | 'onboarding' | 'pitch_deck' | 'interview' | 'data_room' | 'founder';
  verificationLabel: 'founder_provided' | 'extracted' | 'externally_verified';
  /** OPAQUE handles. Never a document, chunk or session id. */
  sourceKeys: string[];
  detailAvailable: boolean;
  founderConfirmed: boolean;
  founderConfirmedAt?: string | null;
  conflictDisposition: 'none' | 'resolved_by_founder' | 'unresolved';
  publicationScope: 'founder_private' | 'data_room_restricted' | 'investor_publishable';
}

export interface StrategyContextVersion {
  id: string;
  version: number;
  context: Record<string, ProvenancedInput>;
  createdAt: string;
}

/** One candidate, as a TEAM MEMBER may see it. Never sent to investors. */
export interface DraftCandidate {
  candidateId: string;
  fieldKey: string;
  value: unknown;
  sourceKind: ProvenancedInput['originLabel'];
  independenceGroup: string;
  provenancePrecision: 'field_exact' | 'aggregate_only';
  assertionStatus: 'observed' | 'extracted' | 'asserted';
  verificationState: 'unverified' | 'founder_confirmed' | 'externally_verified';
  visibility: ProvenancedInput['publicationScope'];
  extractionConfidence?: number;
  /** Other independence groups holding the same value. Corroboration, not proof. */
  corroboratingGroups: string[];
  conflictGroup?: string;
}

export interface StrategyContextDraft {
  startupId: string;
  fields: Record<string, DraftCandidate[]>;
  proposed: Record<string, { candidateId: string; value: unknown } | null>;
  draftFingerprint: string;
  sourceFingerprint: string;
  previousApprovedVersion: { id: string; version: number; sourceFingerprint: string } | null;
  /** Raw sources moved since the last approval. Setup shows "Review needed". */
  sourcesChanged: boolean;
  readiness: Record<string, ServerReadiness>;
}

export interface ServerReadiness {
  status: 'ready' | 'review_needed' | 'missing_required';
  missingRequired: string[];
  unconfirmedRequired: string[];
  conflicts: {
    fieldKey: string;
    conflictGroup: string;
    alternativeSummaries: string[];
    material: boolean;
  }[];
  publicationRequired: string[];
  optionalGaps: string[];
  questions: { fieldKey: string; question: string }[];
}

/** The structured refusal. Carries every blocking category, never a bare message. */
export interface InputNotReady extends ServerReadiness {
  code: 'input_not_ready';
  analysis: AnalysisType | 'all';
}

/**
 * The controlled coverage vocabulary, shared by the decoder and the UI.
 *
 * `policy_limited` says something about OUR position rather than the founder's
 * market: the sources exist and were fetched, and no approved decision permits
 * processing them. Rendering that as `unknown` would blame the market for a
 * licensing gap; rendering it as `skipped` would imply nobody looked.
 */
export const COVERAGE_ELEMENT_STATES = [
  'supported',
  'conflicted',
  'retrieved',
  'policy_limited',
  'unknown',
  'skipped',
  'budget_exhausted',
] as const;
export type CoverageElementState = (typeof COVERAGE_ELEMENT_STATES)[number];

export interface ResearchCoverageView {
  elements: {
    methodologyElement: string;
    planned: number;
    searched: number;
    state: CoverageElementState;
  }[];
  totals: {
    planned: number;
    searched: number;
    supported: number;
    conflicted: number;
    /** Sources came back; nothing has classified them yet. */
    retrieved: number;
    /** Retrieved, and policy does not permit AI processing. */
    policyLimited: number;
    unknown: number;
    skipped: number;
  };
  rounds: number;
  searchCalls: number;
  independenceGroups: number;
  primarySourceCoverage: { targeted: number; achieved: number };
  counterEvidenceCoverage: {
    targeted: number;
    completed: number;
    /** Bounded-search misses. A LIMITATION, never proof of absence. */
    noneFound: number;
    limited: number;
  };
  providerWarnings: { providerId: string; code: string; message: string; affectedElements: string[] }[];
  budgetExhausted?: boolean;
  retrievedAt: string;
}

// ---------------------------------------------------------------------------
// Published report shapes
// ---------------------------------------------------------------------------

/**
 * One retained source, as a permitted reader may see it.
 *
 * `canDisplay` is the field with teeth. Evidence whose licence forbids display
 * may corroborate inside the validator, but the reader gets attribution without
 * the excerpt or the link — showing them anyway would republish material the
 * licence does not cover.
 */
export interface EvidenceView {
  evidenceId: string;
  title?: string;
  publisher?: string;
  canonicalUrl?: string;
  publishedAt?: string;
  retrievedAt?: string;
  excerpt?: string;
  sourceQuality:
    | 'primary_official'
    | 'regulatory'
    | 'statistical'
    | 'company_official'
    | 'reputable_secondary'
    | 'unclassified';
  /** Identity of the source, not of the provider that surfaced it. */
  independenceGroup: string;
  canDisplay: boolean;
}

export type ClaimKind = 'fact' | 'calculation' | 'estimate' | 'inference' | 'point_to_consider' | 'unknown';
export type SupportState = 'supported' | 'partially_supported' | 'conflicted' | 'unsupported' | 'not_verifiable';

/**
 * What one retained source DOES for a claim.
 *
 * `supports` | `weakens` | `conflicts` are SEMANTIC: they say what the source
 * says about the statement, and only the grounding verifier may assign them.
 * `context` is the honest default — retained, attached, visible, asserting no
 * direction.
 *
 * The database has carried this column since the feature shipped and the client
 * threw it away: `toClaimViews` reduced every join to an evidence id, so the
 * source modal said "Supported by the sources below" over material that had
 * never been classified as supporting anything.
 */
export type ClaimSourceRole = 'supports' | 'weakens' | 'conflicts' | 'context';

export interface ClaimSourceLink {
  evidenceId: string;
  role: ClaimSourceRole;
}

export interface ClaimView {
  claimKey: string;
  kind: ClaimKind;
  statement: string;
  supportState: SupportState;
  material: boolean;
  methodologyElement: string;
  /** Every retained source, WITH the role it plays. */
  links: ClaimSourceLink[];
  /**
   * Every linked evidence id, whatever its role.
   *
   * Kept for counts and lookups. It is deliberately NOT a synonym for
   * "supporting evidence" — see `links`.
   */
  evidenceIds: string[];
  /** What the bounded counter-evidence search actually did. Never a verdict. */
  counterEvidenceStatus:
    | 'completed_with_results'
    | 'none_found_after_search'
    | 'provider_failed'
    | 'filter_not_supported'
    | 'budget_exhausted'
    | 'not_required';
}

// ---------------------------------------------------------------------------
// Framework report bodies
// ---------------------------------------------------------------------------
//
// RE-EXPORTED FROM THE CONTRACT, not redeclared.
//
// These were a hand-written "narrow mirror" of the server schemas, and the
// mirror had cracked in both directions: the server wrote fields the client type
// had no name for, and the client type described fields no writer produced
// (`marketBoundary` was one). Deriving them from `report-contract.ts` — the same
// module the edge function validates with — makes a divergence a compile error
// instead of a rendering bug.
//
// The narrowing that mattered is still there, and now it is structural: Porter's
// internal mean has no field in the contract, so no component can render a
// decimal it was never given.




export type CompetitionAxisName = (typeof COMPETITION_AXIS_NAMES)[number];
export type CompetitorClass = (typeof COMPETITOR_CLASS_NAMES)[number];
export type NotReassessedReason = (typeof NOT_REASSESSED_REASONS)[number];
export type PorterForceName = (typeof PORTER_FORCE_NAMES)[number];
export type PorterRating = (typeof PORTER_RATING_NAMES)[number];
export type PestelCategoryName = (typeof PESTEL_CATEGORY_NAMES)[number];
export type PestelPosture = (typeof PESTEL_POSTURE_NAMES)[number];
export type MarketConclusionName = (typeof MARKET_CONCLUSION_NAMES)[number];
export type ExitPathwayName = (typeof EXIT_PATHWAY_NAMES)[number];
export type ExitPathwayTier = (typeof EXIT_PATHWAY_TIER_NAMES)[number];
/** STRATEGIC FIT, not transaction likelihood. The wording is the guardrail. */
export type AcquirerFitTier = (typeof ACQUIRER_FIT_TIER_NAMES)[number];
export type AcquirerFitDimension = (typeof ACQUIRER_FIT_DIMENSION_NAMES)[number];
export type ThesisComparison = (typeof THESIS_COMPARISON_NAMES)[number];

/**
 * The founder's own assumptions. Kept apart from researched evidence.
 *
 * RE-EXPORTED, not redeclared. This file used to carry its own version of the
 * shape and it disagreed with both the editor and the server schema — see
 * `./market-assumptions.ts`, which is now the only declaration and is read by
 * the edge function as well.
 */

// ---------------------------------------------------------------------------
// Exit strategy
// ---------------------------------------------------------------------------
//
// NOTE WHAT IS ABSENT: there is no probability, likelihood or "% chance" field
// anywhere in the contract, and no place to put one. The single most damaging
// thing this feature could produce is a company being told another company will
// probably buy it. It cannot be represented, so it cannot be rendered.

/** Founder-authored. Never independently verified by the act of being saved. */
export interface ExitThesis {
  id?: string;
  version?: number;
  createdAt?: string;
  preferredPathways: ExitPathwayName[];
  openToAssessment: boolean;
  horizonEarliestYear?: number;
  horizonLatestYear?: number;
  targetValuationLow?: number;
  targetValuationHigh?: number;
  targetValuationCurrency?: string;
  buyerCharacteristics?: string[];
  namedCandidates?: { name: string; website?: string }[];
  rationale?: string;
  assumptions?: string[];
  founderSourceUrls?: string[];
}

/** What every analysis view receives. The body differs; the frame does not. */
export interface StrategicReport<TBody> {
  body: TBody;
  claims: ClaimView[];
  sources: EvidenceView[];
  coverage: ResearchCoverageView | null;
  derivation: Record<string, unknown> | null;
  inputSnapshot: {
    strategyContextVersion: number;
    strategyContextVersionId: string;
    marketAssumptionVersion?: number | null;
    exitThesisVersion?: number | null;
    methodologyVersion: string;
    generatedAt?: string | null;
    context: Record<string, ProvenancedInput>;
  } | null;
}
